import React, {useEffect, useRef} from "react";
import {comparators, comparatorsField, funcOrValOr} from "../tools/CommonJS";
import {LoadingAnim, MkSvg} from "../tools/CommonHtml";
import {componentInit, useRerender} from "../tools/CommonReact";
import {Button, Checkbox} from "../tools/Inputs";
import {UIVirtualTable} from "../tools/UIVirtualTable";
import {useSidePanel} from "../tools/UIPopSidePanel";
import {Divider, ListItem, Title} from "./CommonUIComponenets";

export const listSelectModes = {
    all:            Symbol("multi-select, allows and shows select-all and none button"),
    reversible:     Symbol("multi-select, allows and shows select-all and none button. select-all triggers isSelRev."),
    none:           Symbol("multi-select, allows and shows none button."),
    single:         Symbol("single-select, allows and shows none button."),
    noSelect:       Symbol("no selection at all.")
};

export function useListSelectable({list, styleHeight, classNameHeight = "h-[10rem]", classNameListItem, SidePanel, sidePanelClassName, sidePanelProps, emptyMsg,
                                      onClick, dispRowDetails, onPaging, addButtons, selectMode, handleClr, leftTip, deps, isLoading, noSrvCache,
                                      predDrawCheckbox, predOpenSidePanel, rowHeight = 4.1, nonVirtual = false, triggerOpenIdx, selector = listSelsIndexes, dataTest="list-selectable"}) {
    const rerender = useRerender();
    const vals = useRef({showSelsOnly: false}).current;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {setPanel, panelsJsx, closePanel, isPanelOn} = SidePanel? useSidePanel({}) : {};

    if (componentInit(vals, deps ?? [], "_init_sels")) {
        Object.assign(vals, {isSelRev: false});
        selector.init({selObj: vals, selector});
    }

    function select(isAll) {
        selector.clear({selObj: vals});
        if (selectMode === listSelectModes.reversible)
            vals.isSelRev = isAll;
        else if (isAll)
            list.forEach((rowData, index) => (predDrawCheckbox?.(rowData) ?? true) &&
                selector.select({rowData, index, selObj: vals, selector, selectMode}));
        rerender();
    }

    vals.numSelected = () => {
        const nSels = selector.numSelected({selObj: vals});
        return vals.isSelRev ? (list?.length - nSels) || 0 : nSels;
    };

    const onClickHandler = params => onClick? (e => onClick(params)) : SidePanel && (e => onClickRow(params));

    useEffect(() => {
        if (!(triggerOpenIdx >= 0)) return;
        onClickHandler({rowData: list[triggerOpenIdx], index: triggerOpenIdx})?.();
    }, [triggerOpenIdx]);

    function onClickRow({rowData, index}) {
        if (isPanelOn(vals.panelId) && vals.rowData === rowData)
            return vals.rowData = closePanel(vals.panelId);
        vals.rowData = rowData;
        vals.panelId = setPanel({
            Elm: SidePanel, panelId: vals.panelId, obj: rowData, triggerOpenIdx, ...sidePanelProps,
            className: `bg-bg2 w-[29rem] h-[100vh] top-0 right-0 p-4 rounded-l-xl shadow-2xl ${sidePanelClassName}`
        });
    }

    // returns an onClick function only if current state is not already desired state.
    const toggleShowSels = toState => vals.showSelsOnly !== toState && (e => ((vals.showSelsOnly = toState), rerender()));

    function ShowSelecteds() {
        const nSels = vals.numSelected();
        const items = selector.getSelItems({selObj: vals, list});
        items.sort(comparatorsField(comparators.numberSig, "index"));

        return <div data-test="list-selectable" className="flex flex-col gap-2 h-full">
            {!nSels? "No items selected." : vals.isSelRev && nSels === list?.length? "All items selected." : <>
                {!!vals.isSelRev && "All except:"}
                {nonVirtual?
                    <div>{items.map(({rowData, index}, actlIndex) =>
                        rowRenderer({rowData, index, actlIndex}))}</div> :
                    <UIVirtualTable
                        rows={items}
                        rowHeight={rowHeight}
                        className={`grow pr-1 overflow-hidden ${classNameHeight}`}
                        rowRenderer={({rowData: {rowData, index}, index: actlIndex}) => rowRenderer({rowData, index, actlIndex})}
                        styleHeight={styleHeight}
                    />}
            </>}
        </div>;
    }

    const rowRenderer = ({rowData, index, actlIndex}) =>
        <ListItem
            data-test={`${index}-row-renderer-list-item`}
            key={actlIndex ?? index} className={classNameListItem}
            onClick={(predOpenSidePanel?.(rowData) ?? true) && onClickHandler({rowData, index}) || undefined}
            active={vals.rowData === rowData && isPanelOn(vals.panelId)}
            handleClr={funcOrValOr(handleClr, rowData)}
            leftTip={funcOrValOr(leftTip, rowData)}
        >
            {selectMode !== listSelectModes.noSelect &&
                <div className="shrink-0 w-5 h-full">
                    {(predDrawCheckbox?.(rowData) ?? true) &&
                        <Checkbox
                            className="h-full"
                            isChecked={!!selector.isSelected({rowData, index, selObj: vals}) !== vals.isSelRev}
                            onChange={e => {
                                selector.select({rowData, index, selObj: vals, selector, selectMode});
                                rerender();
                            }}/>}
                </div>}
            {dispRowDetails?.(rowData)}
        </ListItem>;
    
    return {
        selObj: vals,
        listJsx:
            <div className={`flex flex-col gap-3 h-full`}>
                <div className="shrink-0 items-center flex text-sm border-handle gap-1">
                    {!!list?.length &&
                        <Button data-test="list-selectable-total" h={<><span className="opacity-80">Total:&nbsp;</span>{list?.length.toLocaleString()}</>}
                                className={vals.showSelsOnly && "text-prm-light"}
                                onClick={selectMode !== listSelectModes.noSelect? toggleShowSels(false) : undefined}/>}
                    {!!list?.length && selectMode !== listSelectModes.noSelect && <>
                        <Divider vertical className="mx-1.5"/>
                        <Button data-test="list-selectable-selected-count" h={<>{vals.numSelected().toLocaleString()}<span className="opacity-80">&nbsp;selected</span></>}
                                className={!vals.showSelsOnly && "text-prm-light"} onClick={toggleShowSels(true)}/>
                        {[listSelectModes.all, listSelectModes.reversible].includes(selectMode) && <>
                            <Divider vertical className="mx-1.5"/>
                            <Button data-test="list-selectable-select-all" h="Select all" className="text-sm text-prm-light" onClick={e => select(true)}/>
                        </>}
                        <Divider vertical className="mx-1.5"/>
                        <Button data-test="list-selectable-select-none" h="none" className="text-sm text-prm-light" onClick={e => select(false)}/>
                    </>}
                    {list && isLoading && <div className="grow flex justify-end mr-2"><LoadingAnim classNameSize="w-5 h-5"/></div>}
                    {addButtons}
                </div>
                {vals.showSelsOnly?
                    <ShowSelecteds/> :
                    !list? <LoadingAnim noSrvCache={noSrvCache} classNameSize="w-10 h-10"/> :
                        !list.length?
                            <div className="ml-8 text-tx-steel">{emptyMsg ?? "(None)"}</div> :
                            nonVirtual?
                                <div>{list.map((rowData, index) => rowRenderer({rowData, index}))}</div> :
                                <UIVirtualTable
                                    rows={list} rowHeight={rowHeight} dataTest={dataTest}
                                    className={`grow pr-1 overflow-hidden ${classNameHeight}`}
                                    rowRenderer={rowRenderer}
                                    onSizing={onPaging}
                                    styleHeight={styleHeight}
                                />}
                {panelsJsx}
            </div>
    };
}


export function SidePanelContainer({onClose, title, children}) {
    return (
        <div data-test="side-panel-container" className="flex flex-col h-full gap-2 overflow-y-scroll scrollbar-none">
            <div data-test="side-panel-container-headers" className="flex justify-between">
                <Title className="py-2 text-xl">{title}</Title>
                <Button data-test="side-panel-container-close" onClick={onClose} className="text-tx-cool-grey -mr-1"><MkSvg className="h-7 w-7" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></Button>
            </div>
            <Divider classNameColor="bg-bg4 mb-2 shrink-0"/>
            {children}
        </div>
    );
}


export const listSelsIndexes = {
    init: ({selObj}) => selObj.sels = new Set(),
    clear: ({selObj}) => selObj.sels?.clear(),
    numSelected: ({selObj}) => selObj.sels.size,
    isSelected: ({index, selObj}) => selObj.sels.has(index),
    select: ({selObj, index, selectMode, selector}) => {
        if (!selObj.sels.delete(index)) {
            if (selectMode === listSelectModes.single)
                selector.clear({selObj});
            selObj.sels.add(index);
        }
    },
    getSelection: ({selObj}) => selObj.sels,
    getSelItems: ({selObj, list}) => [...selObj.sels].map(index => ({rowData: list?.[index] ?? index, index})),
};

export const listSelsDevice = {
    init: ({selObj, selector}) => selector.clear({selObj}),
    clear: ({selObj}) => Object.assign(selObj, {selsByDev: new Set(), selsByGuid: new Set()}),
    numSelected: ({selObj}) => (selObj.selsByDev.size) + (selObj.selsByGuid.size),
    isSelected: ({rowData, selObj}) => selObj.selsByDev.has(rowData.device) || selObj.selsByGuid.has(rowData.deviceGuid),
    select: ({rowData, selObj, selector}) => {
        if (!selObj.selsByDev.delete(rowData.device)) {
            if (rowData.deviceGuid)
                selObj.selsByGuid.toggle(rowData.deviceGuid);
            else
                selObj.selsByDev.add(rowData.device);
        }
    },
    getSelection: ({selObj}) => ({
        list: [...selObj.selsByDev],
        listGuids: [...selObj.selsByGuid],
    }),
    getSelItems: ({selObj, list}) => {
        const itmKey = k => v => {
            let index = list?.findIndex(e => e?.[k] === v);
            if (!(index >= 0)) index = undefined;
            return ({rowData: list?.[index] ?? {[k]: v}, index});
        }
        return [
            ...[...selObj.selsByDev].map(itmKey("device")),
            ...[...selObj.selsByGuid].map(itmKey("deviceGuid"))
        ];
    },
};

export const listSelsParam = {
    init: ({selObj, selector}) => selector.clear({selObj}),
    clear: ({selObj}) => selObj.selsByParam = {},
    numSelected: ({selObj}) => Object.keys(selObj.selsByParam).length,
    isSelected: ({rowData, selObj}) => selObj.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`],
    select: ({rowData, selObj, selector}) => {
        if (selObj.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`])
            delete selObj.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`];
        else
            selObj.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`] = [rowData.param, rowData.paramExtra];
    },
    getSelection: ({selObj}) => ({
        list: Object.values(selObj.selsByParam).map(v => v[0]),
        listParamExtra: Object.values(selObj.selsByParam).map(v => v[1]),
    }),
    getSelItems: ({selObj, list}) => Object.values(selObj.selsByParam).map(v => {
        let index = list?.findIndex(e => e?.param === v[0] && e?.paramExtra === v[1]);
        if (!(index >= 0)) index = undefined;
        return ({rowData: list?.[index] ?? {param: v[0], paramExtra: v[1]}, index});
    }),
};

export const listSelsKey = key => ({
    init: ({selObj}) => selObj.sels = new Set(),
    clear: ({selObj}) => selObj.sels?.clear(),
    numSelected: ({selObj}) => selObj.sels.size,
    isSelected: ({rowData, selObj}) => selObj.sels.has(rowData[key]),
    select: ({rowData, selObj, selectMode, selector}) => {
        if (!selObj.sels.delete(rowData[key])) {
            if (selectMode === listSelectModes.single)
                selector.clear({selObj});
            selObj.sels.add(rowData[key]);
        }
    },
    getSelection: ({selObj}) => selObj.sels,
    getSelItems: ({selObj, list}) => [...selObj.sels]
        .map(k => ({rowData: list?.find(e => e?.[key] === k)}))
        .filter(e => e.rowData),
});
